/**
 * Admin V3 — Operations / Health — Service status card
 *
 * Renders one service (MongoDB or Redis) with:
 *   - Service name
 *   - Status pill (healthy / unhealthy / unavailable / mock)
 *   - Connected indicator (text + dot)
 *   - Error message (when status reports an error)
 *
 * Pure presentation; data comes from /health/extended payload's `services` map.
 */

import React from 'react';
import { Database, CircleDot, ServerCrash } from 'lucide-react';
import {
  cn,
  SmallPill,
  serviceStatusMeta,
} from '../../shared/visualTokens';
import { t } from '../../i18n';

const OperationsHealthServiceCard = ({ serviceKey, serviceName, status, locale = 'fr' }) => {
  const statusKey = status?.status || 'unavailable';
  const meta = serviceStatusMeta[statusKey] || {
    label: statusKey,
    pill: 'bg-slate-100 text-slate-500 border border-slate-200',
    alert: true,
  };
  const connected = Boolean(status?.connected);
  const errorMessage = status?.error || null;

  const isAlert = meta.alert;
  const Icon = isAlert ? ServerCrash : Database;

  return (
    <div
      className={cn(
        'rounded-2xl border bg-white p-5 shadow-[0_4px_18px_rgba(15,23,42,0.04)]',
        isAlert ? 'border-rose-200' : 'border-slate-200'
      )}
      data-testid={`adminv3-operations-health-service-${serviceKey}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2.5">
          <div
            className={cn(
              'flex h-9 w-9 items-center justify-center rounded-lg',
              isAlert ? 'bg-rose-100 text-rose-600' : 'bg-emerald-100 text-emerald-600'
            )}
          >
            <Icon className="h-4 w-4" />
          </div>
          <div>
            <h4 className="text-[15px] font-semibold text-slate-900">{serviceName}</h4>
            <div className="flex items-center gap-1.5 mt-0.5">
              <CircleDot
                className={cn(
                  'h-3 w-3',
                  connected ? 'text-emerald-500' : 'text-slate-300'
                )}
              />
              <span className="text-[11px] text-slate-500">
                {connected
                  ? t('operations.health.services.connected',    locale)
                  : t('operations.health.services.disconnected', locale)}
              </span>
            </div>
          </div>
        </div>
        <SmallPill meta={meta} />
      </div>

      {errorMessage && (
        <div className="mt-2 rounded-lg bg-rose-50/60 border border-rose-200 p-2">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-rose-500 mb-0.5">
            {t('operations.health.services.errorLabel', locale)}
          </div>
          <div className="text-[12px] text-rose-700 font-mono break-all">
            {errorMessage}
          </div>
        </div>
      )}
    </div>
  );
};

export default OperationsHealthServiceCard;
